import React, {useState, useRef, useEffect} from 'react';
import usePrevious from '../../../helpers/UsePrevious';
import SpinnerLoader from '../../SpinnerLoader/SpinnerLoader';
import PlaylistItemMetric from '../../../helpers/PlaylistItemMetric';
import Debug from '../../../helpers/Debug';
import {LOG_INACTIVITY_TIME} from '../../../config/App';
import './PromotionalVideo.css';

// MARK: Regarding audio playback in the browser...
// When the mute option is disabled (meaning audio will be reproduced), the browser will require user interaction to start the video, even if 
// the video has no audio at all. If no interaction occurs, the video will stay paused at the first frame. Its not very clear at the moment 
// what will reset the permissions, because sometimes it will work even after restarting the computer, and other times it will fail due 
// to no interaction. More info: https://developer.chrome.com/blog/autoplay/

function PromotionalVideo({playlistId, itemId, id, url, loop, muted, start, onPlaybackComplete, getMetadata}) {
    const [readyToPlay, setReadyToPlay] = useState(false);
    const [error, setError] = useState(false);
    const previousUrl = usePrevious(url);
    const videoRef = useRef(null);
    const lastTimeRef = useRef(0);

    useEffect(() => {
        if (previousUrl !== url) {
            setReadyToPlay(false);
            setError(false);
        }
    }, [url]);

    const onVideoLoad = () => {
        setReadyToPlay(true);
    };

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            if (start && readyToPlay) {
                video.currentTime = 0;
                video.volume = 0.5;
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        if (LOG_INACTIVITY_TIME) Debug.printToLog('info', `Video ${id} | Playback started, duration: ${Math.round(video.duration)} seconds`);
                        incrementViewCount();
                    })
                    .catch((error) => {
                        Debug.printToLog('error', `Video ${id} | Playback error: ${error.code} - ${error.message}`);
                    });
                }
            } else {
                video.pause();
                video.volume = 0;
            }
        }
    }, [start, readyToPlay]);

    const onVideoError = () => {
        const video = videoRef.current;
        if (video) {
            const error = video.error;
            Debug.printToLog('error', `Video ${id} | Playback error: ${error.code} - ${error.message}`);
        }
        setError(true);
    };

    useEffect(() => {
        if (start && error) finishPlayback();
    }, [start, error]);

    const onVideoTimeUpdate = () => {
        const video = videoRef.current;
        if (video) {
            const lastTime = lastTimeRef.current;
            const currentTime = video.currentTime;
            if (loop && currentTime < lastTime) incrementViewCount(); // Video is starting a new loop.
            lastTimeRef.current = currentTime;
        }
    };

    const onVideoEnd = () => {
        if (LOG_INACTIVITY_TIME) Debug.printToLog('info', `Video ${id} | Playback ended`);
        finishPlayback();
    };

    const finishPlayback = () => {
        if (onPlaybackComplete) onPlaybackComplete();
    };

    const incrementViewCount = () => {
        const metadata = getMetadata();
        const client = metadata ? metadata.client : undefined;
        const instance = metadata ? metadata.instance : undefined;
        PlaylistItemMetric.incrementViewCount(playlistId, itemId, 'video', client, instance);
    };

    const loaderStyle = () => {
        return {
            opacity: start && readyToPlay ? 0 : 1
        };
    };

    return (
        <React.Fragment>
            <div className='promotional-video-loader-cover' style={loaderStyle()}>
                <div className='promotional-video-loader'>
                    <SpinnerLoader size={100} color='#ffffff'/>
                </div>
            </div>
            <div className='promotional-video'>
                <video
                    ref={videoRef}
                    src={url}
                    type='video/mp4'
                    width='100%'
                    height='100%'
                    autoPlay={false}
                    playsInline={true}
                    controls={false}
                    muted={muted}
                    loop={loop}
                    onCanPlayThrough={onVideoLoad}
                    onTimeUpdate={onVideoTimeUpdate}
                    onEnded={onVideoEnd}
                    onError={onVideoError}
                    preload='auto'
                />
            </div>
        </React.Fragment>
    );
}

export default PromotionalVideo;